import {
  ActionIcon,
  Affix,
  Button,
  FileButton,
  Group,
  Image,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';

import { fetchOk } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

const CATEGORY_OPTIONS = [
  { value: 'idea', label: 'Idea or suggestion' },
  { value: 'bug', label: 'Something looks broken' },
  { value: 'copy', label: 'Wording / copy' },
  { value: 'design', label: 'Design / layout' },
  { value: 'praise', label: 'Something I like' },
  { value: 'other', label: 'Something else' },
] as const;

// Mirror the server caps (worker/routes/feedback.ts) so we can reject early
// with a friendly message instead of a 400 round-trip.
const MAX_SCREENSHOTS = 3;
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_SCREENSHOT_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const SCREENSHOT_ACCEPT = ALLOWED_SCREENSHOT_MIME.join(',');

type FormValues = {
  category: string;
  message: string;
  email: string;
};

/**
 * FeedbackLauncher — a floating "Feedback" button, admin-hat only (Richie's
 * own review tool; the wider public entry point was retired at launch,
 * 2026-08-26 — the /api/feedback route + admin triage queue stayed live
 * throughout). Opens a modal that posts to the public, CSRF-exempt
 * /api/feedback endpoint.
 *
 * Mounted once at the app root (main.tsx) as a sibling overlay so it sits above
 * all four shells, unconditionally — gating on `activeHat` happens here so a
 * hat switch shows/hides it without remounting. Reads window.location at
 * submit time, so it needs no router context. A desktop convenience hotkey
 * (Cmd/Ctrl+Shift+F) also opens it, only while admin-hatted.
 *
 * Screenshots: the admin can attach up to three images, either by picking files
 * or by pasting (Cmd/Ctrl+V) a screenshot straight from the clipboard while the
 * modal is open. Attaching any switches the submit to multipart/form-data.
 */
export function FeedbackLauncher() {
  const { user, activeHat } = useAuth();
  const [opened, { open, close }] = useDisclosure(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  // Object URLs for the local thumbnail previews, derived from `screenshots`.
  const previews = useMemo(() => screenshots.map((f) => URL.createObjectURL(f)), [screenshots]);

  const form = useForm<FormValues>({
    initialValues: { category: 'idea', message: '', email: '' },
    validate: {
      message: (v) => (v.trim().length < 3 ? 'Tell us a little more.' : null),
      email: (v) =>
        user === null && v.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
          ? "That email doesn't look right."
          : null,
    },
  });

  // Revoke the previous batch of object URLs whenever they change (or on
  // unmount) so the thumbnails never leak blob URLs.
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  function addScreenshots(picked: File[]): void {
    if (picked.length === 0) return;
    setScreenshots((prev) => {
      const room = MAX_SCREENSHOTS - prev.length;
      if (room <= 0) {
        showToast({ title: 'Screenshot limit reached', body: `Up to ${MAX_SCREENSHOTS} screenshots.`, kind: 'error' });
        return prev;
      }
      const accepted: File[] = [];
      for (const f of picked.slice(0, room)) {
        if (!ALLOWED_SCREENSHOT_MIME.includes(f.type.toLowerCase())) {
          showToast({ title: 'Unsupported image', body: `${f.name || 'That file'} isn't a supported image.`, kind: 'error' });
          continue;
        }
        if (f.size > MAX_SCREENSHOT_BYTES) {
          showToast({ title: 'Too large', body: `${f.name || 'That image'} is over the 5 MB limit.`, kind: 'error' });
          continue;
        }
        accepted.push(f);
      }
      return accepted.length > 0 ? [...prev, ...accepted] : prev;
    });
  }

  function removeScreenshotAt(index: number): void {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  }

  // Desktop convenience hotkey — Cmd/Ctrl+Shift+F. Guarded so it never fires
  // while the visitor is typing in a field (mirrors home/StepNavigator).
  useEffect(() => {
    if (activeHat !== 'admin') return;
    const handler = (e: KeyboardEvent): void => {
      if (!(e.key === 'f' || e.key === 'F') || !e.shiftKey || !(e.metaKey || e.ctrlKey)) return;
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      open();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, activeHat]);

  // Paste-to-attach — while the modal is open, a clipboard paste carrying image
  // data adds it as a screenshot. Text paste into the textarea is untouched
  // (we only act when the clipboard actually holds an image).
  useEffect(() => {
    if (!opened) return;
    const handler = (e: ClipboardEvent): void => {
      const items = e.clipboardData?.items;
      if (items === undefined) return;
      const images: File[] = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file !== null) images.push(file);
        }
      }
      if (images.length === 0) return;
      e.preventDefault();
      addScreenshots(images);
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [opened]);

  function resetAndClose(): void {
    form.reset();
    setScreenshots([]);
    close();
  }

  async function handleSubmit(values: FormValues): Promise<void> {
    setSubmitting(true);
    try {
      const email = values.email.trim();
      const includeEmail = user === null && email.length > 0;

      if (screenshots.length > 0) {
        // Multipart path — text fields + screenshot files.
        const body = new FormData();
        body.set('category', values.category);
        body.set('message', values.message);
        if (includeEmail) body.set('email', email);
        body.set('page_url', window.location.href);
        body.set('user_agent', navigator.userAgent);
        for (const file of screenshots) {
          body.append('screenshots[]', file, file.name || 'screenshot.png');
        }
        await fetchOk('/api/feedback', { method: 'POST', credentials: 'include', body });
      } else {
        await fetchOk('/api/feedback', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: values.category,
            message: values.message,
            ...(includeEmail ? { email } : {}),
            page_url: window.location.href,
            user_agent: navigator.userAgent,
          }),
        });
      }

      resetAndClose();
      showToast({ title: 'Feedback sent', body: 'Your note goes straight to the team.', kind: 'success' });
    } catch {
      showToast({ title: 'Could not send', body: 'Something went wrong sending that. Please try again.', kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const canAddMore = screenshots.length < MAX_SCREENSHOTS;

  // Production: admins only (Richie's request, 2026-09-07). Preview builds
  // (VITE_WWAAVE_PREVIEW, set by scripts/preview/deploy.mjs) show it to every
  // role — testers switch hats, and previews sit behind Cloudflare Access.
  if (activeHat !== 'admin' && import.meta.env.VITE_WWAAVE_PREVIEW !== '1') return null;

  return (
    <>
      <Affix position={{ bottom: 20, left: 20 }}>
        <Button
          onClick={open}
          radius="xl"
          size="sm"
          variant="filled"
          color="tide"
          aria-label="Send feedback"
        >
          Feedback
        </Button>
      </Affix>

      <Modal opened={opened} onClose={resetAndClose} title="Send feedback" centered>
        <form onSubmit={form.onSubmit((v) => void handleSubmit(v))}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Tell us what's working, what isn't, or what you'd like to see. We read every note.
            </Text>
            <Select
              label="What's this about?"
              data={CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              allowDeselect={false}
              {...form.getInputProps('category')}
            />
            <Textarea
              label="Your feedback"
              placeholder="What's on your mind?"
              autosize
              minRows={4}
              maxRows={10}
              maxLength={4000}
              {...form.getInputProps('message')}
            />

            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>
                  Screenshots <Text span size="xs" c="dimmed">(optional)</Text>
                </Text>
                <FileButton
                  multiple
                  accept={SCREENSHOT_ACCEPT}
                  onChange={(picked) => addScreenshots(picked)}
                  disabled={submitting || !canAddMore}
                >
                  {(props) => (
                    <Button {...props} size="xs" variant="light" color="tide" disabled={submitting || !canAddMore}>
                      Add image
                    </Button>
                  )}
                </FileButton>
              </Group>
              <Text size="xs" c="dimmed">
                Paste a screenshot (Ctrl/Cmd+V) or add up to {MAX_SCREENSHOTS} images.
              </Text>
              {previews.length > 0 ? (
                <Group gap="xs" wrap="wrap">
                  {previews.map((src, i) => (
                    <div key={src} style={{ position: 'relative' }}>
                      <Image src={src} alt={screenshots[i]?.name ?? 'Screenshot'} h={64} w={64} radius="sm" fit="cover" />
                      <ActionIcon
                        variant="filled"
                        color="dark"
                        size="xs"
                        radius="xl"
                        aria-label={`Remove screenshot ${i + 1}`}
                        onClick={() => removeScreenshotAt(i)}
                        disabled={submitting}
                        style={{ position: 'absolute', top: -6, right: -6 }}
                      >
                        ×
                      </ActionIcon>
                    </div>
                  ))}
                </Group>
              ) : null}
            </Stack>

            {user === null ? (
              <TextInput
                label="Email (optional)"
                placeholder="you@example.com"
                description="Leave this if you'd like us to be able to follow up."
                {...form.getInputProps('email')}
              />
            ) : null}
            <Button type="submit" loading={submitting} color="tide">
              Send feedback
            </Button>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
